import { CREATE_ACCOUNT_FAIL, CREATE_ACCOUNT_REQUEST, CREATE_ACCOUNT_SUCCESS } from './createAccount';
import { GeoCoordinates, UserData } from '@/types/UserData';
import { LOAD_BIDDER_DATA_FAIL, LOAD_BIDDER_DATA_REQUEST, LOAD_BIDDER_DATA_SUCCESS, LOG_OUT_BIDDER } from './actions';
import { LOGIN_FAIL, LOGIN_REQUEST, LOGIN_SUCCESS } from './login';
import { PASSWORD_RESET_REQUEST, PASSWORD_RESET_SUCCESS } from './passwordReset';
import caseInsensitiveCompare from '@liveauctioneers/caterwaul-components/lib/utils/caseInsensitiveCompare';
import { CONFIRM_PASSWORD_SUCCESS } from './confirmPassword';
import Cookies from 'universal-cookie';
import { createSelector } from 'reselect';
import { fetchBidderCardStatusIfNeeded } from './bidderCardStatus';
import { fetchBidderData } from '@/redux/api/bidder';
import { fetchCertificatesIfNeeded } from './certCapture';
import { fetchConversationFoldersIfNeeded } from './conversationFolders';
import { fetchSmsAlertsIfNeeded } from './smsAlerts';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import { HYDRATE_CLIENT_STATE } from './client';
import jwtdecode from 'jwt-decode';
import { MainhostLogin } from '@/types/MainhostLogin';
import ms from 'ms';
import { OPEN_CONFIRM_PASSWORD_MODAL_FROM_CHECKER } from './shouldLogin';
import { recordAnalyticsForIdentify } from './analytics';
import resellerTypes from '@/enums/resellerTypes';
import { SUBMIT_CONSENT_SUCCESS } from './consent';
import { v4 as uuidv4 } from 'uuid';

const cookie = new Cookies();
const REDUX_STORE_TIME = ms('30m');
const UNREGISTERED = 'unregistered';
const PARTIALLYREGISTEREDBIDDER = 'PartiallyRegisteredBidder';
const REGISTEREDBIDDER = 'bidder';

/* reducer */
export const DEFAULT_STATE = {
    authName: '',
    authNuid: '',
    authVerify: '',
    confirmPasswordRequiredTriggered: false,
    loaded: 0,
    loading: false,
    mobile: '',
    token: '',
    userData: {},
};

export type State = typeof DEFAULT_STATE & {
    userData: UserData | {};
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let time;

    switch (action.type) {
        case CREATE_ACCOUNT_SUCCESS:
            time = action.meta.actionTime;
            return {
                ...state,
                loaded: time,
                loading: false,
                token: action.payload,
            };
        case LOGIN_SUCCESS:
        case CONFIRM_PASSWORD_SUCCESS:
        case PASSWORD_RESET_SUCCESS:
            time = action.meta.actionTime;

            if (typeof action.payload.annualNMS !== 'undefined') {
                action.payload.bidder.ANMS = action.payload.annualNMS;
            }

            return {
                ...state,
                authName: action.payload.mainhostLogin['5Xq_name'],
                authNuid: action.payload.mainhostLogin['5Xq_nuid'],
                authVerify: action.payload.mainhostLogin['5Xq_nverify'],
                confirmPasswordRequiredTriggered: false,
                loaded: time,
                loading: false,
                token: action.payload.token,
                userData: {
                    ...action.payload.bidder,
                    bidderId: Number(action.payload.bidder.bidderId),
                },
            };
        case LOAD_BIDDER_DATA_REQUEST:
        case LOGIN_REQUEST:
        case CREATE_ACCOUNT_REQUEST:
        case PASSWORD_RESET_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_BIDDER_DATA_SUCCESS:
            time = action.meta.actionTime;

            if (typeof action.payload.annualNMS !== 'undefined') {
                action.payload.bidder.ANMS = action.payload.annualNMS;
            }

            return {
                ...state,
                loaded: time,
                loading: false,
                userData: {
                    ...action.payload.bidder,
                    bidderId: Number(action.payload.bidder.bidderId),
                },
            };
        case OPEN_CONFIRM_PASSWORD_MODAL_FROM_CHECKER:
            return {
                ...state,
                confirmPasswordRequiredTriggered: true,
            };
        case CREATE_ACCOUNT_FAIL:
        case LOG_OUT_BIDDER:
        case LOGIN_FAIL:
            return DEFAULT_STATE;
        case SUBMIT_CONSENT_SUCCESS:
            return {
                ...state,
                userData: {
                    ...state.userData,
                    acceptedTermsOfService: action.payload,
                },
            };
        case HYDRATE_CLIENT_STATE:
            return {
                ...state,
                authName: action.payload.authName,
                authNuid: action.payload.authNuid,
                authVerify: action.payload.authVerify,
                token: action.payload.token,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.user;

export const userDataSelector: (state: GlobalState) => any = createSelector(stateSelector, (state) => state.userData);

export const getANMS: (state: GlobalState) => number = createSelector(userDataSelector, (userData) => {
    return userData.ANMS ? userData.ANMS : 0;
});

export const getAuthToken: (state: GlobalState) => string = createSelector(stateSelector, (state) => state.token);

export const getBidderEmail: (state: GlobalState) => string = createSelector(userDataSelector, (userData) => {
    return userData.email ? userData.email : '';
});

export const getBidderPhoneNumber: (state: GlobalState) => string = createSelector(userDataSelector, (userData) => {
    return userData.mobileNumber ? userData.mobileNumber : userData.phoneNumber ? userData.phoneNumber : '';
});

export const isTermsOfServiceUnknown: (state: GlobalState) => boolean = createSelector(userDataSelector, (userData) => {
    return userData.acceptedTermsOfService === 'unknown';
});

export const getBidderCountry: (state: GlobalState) => string = createSelector(userDataSelector, (userData) => {
    return userData.country ? userData.country : '';
});

export const getBidderState: (state: GlobalState) => string = createSelector(userDataSelector, (userData) => {
    return userData.state ? userData.state : '';
});

export const getBidderInUSA = createSelector(
    getBidderCountry,
    (country: string = '') => caseInsensitiveCompare(country, 'us') || caseInsensitiveCompare(country, 'united states')
);

export const isBidderInContiguousUS: (state: GlobalState) => boolean = createSelector(
    [getBidderCountry, getBidderState],
    (country, state) => {
        const nonContiguousState = [
            'Hawaii',
            'HI',
            'Alaska',
            'AK',
            'American Samoa',
            'AS',
            'Virgin Islands',
            'VI',
            'Northern Mariana Islands',
            'MP',
            'Guam',
            'GU',
            'Puerto Rico',
            'PR',
        ];
        return ['United States', 'US'].includes(country) && !nonContiguousState.includes(state);
    }
);

export const getBidderType: (state: GlobalState) => string = createSelector(getAuthToken, (token) => {
    if (token) {
        try {
            // TODO: see if this try catch is enough.  We shouldn't throw if something is wrong
            return jwtdecode(token).type;
        } catch (error) {
            return UNREGISTERED;
        }
    }
    return UNREGISTERED;
});

export const isConfirmPasswordTriggered: (state: GlobalState) => boolean = createSelector(
    stateSelector,
    (state) => state.confirmPasswordRequiredTriggered
);

export const isUserPartiallyRegistered: (state: GlobalState) => boolean = createSelector(
    getBidderType,
    (bidderType) => {
        return bidderType === PARTIALLYREGISTEREDBIDDER;
    }
);

const isUserRegistered: (state: GlobalState) => boolean = createSelector(getBidderType, (bidderType) => {
    return bidderType === REGISTEREDBIDDER;
});

const getAuthNuid: (state: GlobalState) => string = createSelector(stateSelector, (state) => state.authNuid || '');

const validateBidderId = (id: number) => {
    if (typeof id !== 'number' || isNaN(id) || !id) {
        return -1;
    }
    return id;
};

export const getBidderId: (state: GlobalState) => number = createSelector(
    [getAuthNuid, userDataSelector, getAuthToken],
    (authNuid, userData, token) => {
        if (!userData?.bidderId) {
            if (token) {
                try {
                    const decoded = jwtdecode(token);
                    if (decoded.type === 'bidder') {
                        return validateBidderId(Number(decoded.sub));
                    }
                } catch (error) {
                    console.warn('Failed to decode a potentially valid token', error, token);
                }
            }

            const bidderIdMatches = authNuid.match(/(^[\d]+):::/);
            if (bidderIdMatches && bidderIdMatches.length > 1) {
                return validateBidderId(Number(bidderIdMatches[1]));
            }
            return -1;
        }

        return validateBidderId(Number(userData.bidderId));
    }
);

export const isUserLoggedIn: (state: GlobalState) => boolean = createSelector(
    getAuthToken,
    getBidderId,
    isUserRegistered,
    (token, bidderId, registered) => {
        return Boolean(token) && registered && bidderId > 0;
    }
);

export const isUserAtLeastPartiallyRegistered: (state: GlobalState) => boolean = createSelector(
    isUserPartiallyRegistered,
    isUserLoggedIn,
    (registered, loggedIn) => registered || loggedIn
);

export const getUserData: (state: GlobalState) => any = createSelector(
    isUserAtLeastPartiallyRegistered,
    userDataSelector,
    (userAtLeastPartiallyRegistered, userData) => {
        return userAtLeastPartiallyRegistered ? userData : {};
    }
);

export const getUserIsInGoodStanding: (state: GlobalState) => boolean = createSelector(getUserData, (userData) => {
    const standing = userData.standing || '';
    return standing === '' || standing === 'y' || standing === 'good';
});

export const getBidderIsSuspended: (state: GlobalState) => boolean = createSelector(getUserData, (userData) => {
    const standing = userData.standing || '';
    return standing === 's';
});

export const getUserFullName: (state: GlobalState) => string = createSelector(getUserData, (userData) => {
    const first = userData.firstName || '';
    const last = userData.lastName || '';
    return `${first} ${last}`.trim();
});

export const getUserFirstName: (state: GlobalState) => string = createSelector(getUserData, (userData) => {
    const first = userData.firstName || '';
    return first.trim();
});

export const getZipCodeGeoCoordinates: (state: GlobalState) => GeoCoordinates = createSelector(
    getUserData,
    (userData) => {
        return userData.zipCodeGeoCoordinates;
    }
);

export const isBidderLoaded: (state: GlobalState) => boolean = createSelector(
    userDataSelector,
    (userData) => userData?.bidderId > 0
);

export const isProfileComplete: (state: GlobalState) => boolean = createSelector(userDataSelector, (userData) => {
    return userData ? Boolean(userData.profileComplete) : false;
});

const getLoadTimeForUserData: (state: GlobalState) => number = createSelector(
    stateSelector,
    (state) => state.loaded || 0
);

export const isUserDataLoading: (state: GlobalState) => boolean = createSelector(
    stateSelector,
    (state) => state.loading || false
);

export const getUserDataLoaded: (state: GlobalState) => boolean = createSelector(
    stateSelector,
    (state) => state.loaded > 0 || false
);

export const getSessionId: () => string = () => `${cookie.get('_session_id') || uuidv4()}`;

export const getUserPreferredCurrency: (state: GlobalState) => string = createSelector(
    userDataSelector,
    (state) => state.preferredCurrency || 'USD'
);

const shouldFetchUserData = (state: GlobalState) => {
    const userAtLeastPartiallyRegistered = isUserAtLeastPartiallyRegistered(state);
    if (!userAtLeastPartiallyRegistered) {
        return false;
    }

    const userData = getUserData(state);
    if (userData) {
        const loaded = getLoadTimeForUserData(state);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    const loading = isUserDataLoading(state);
    return !loading;
};

export const getMainhostLogin: (state: GlobalState) => MainhostLogin = createSelector(stateSelector, (state) => {
    return {
        authName: state.authName,
        authNuid: state.authNuid,
        authVerify: state.authVerify,
    };
});

/* ACTION CREATORS */
export const logOutBidder = () => ({
    type: LOG_OUT_BIDDER,
});

const loadBidderData = ({ authToken, deployment }) => async (dispatch) => {
    try {
        dispatch({
            payload: authToken,
            type: LOAD_BIDDER_DATA_REQUEST,
        });
        const response = await fetchBidderData({ authToken, deployment });

        dispatch(fetchConversationFoldersIfNeeded());
        dispatch(fetchSmsAlertsIfNeeded());
        dispatch(fetchBidderCardStatusIfNeeded());
        if (response.data.resellerType === resellerTypes.RESELLER) {
            dispatch(fetchCertificatesIfNeeded());
        }

        dispatch({
            meta: { actionTime: Date.now() },
            payload: response.data,
            type: LOAD_BIDDER_DATA_SUCCESS,
        });
        dispatch(recordAnalyticsForIdentify());

        return Promise.resolve();
    } catch (error) {
        dispatch({
            error: true,
            meta: { token: authToken },
            payload: error,
            type: LOAD_BIDDER_DATA_FAIL,
        });
        dispatch(logOutBidder());
    }
};

export const fetchBidderDataIfNeeded = (token?: string | null | undefined, force?: boolean) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    const authToken = token || getAuthToken(state);
    const deployment = getDeployment(state);
    if (Boolean(authToken) && (force || shouldFetchUserData(state))) {
        await dispatch(loadBidderData({ authToken, deployment }));
    }
    return Promise.resolve();
};
